// Meutant colour gradients: the slowly flowing background in a product's colours, shared by
// the homepage, category pages, product pages and the basket.
if (!window.MeuGradient) {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Colours ---------- */

  const parseHex = (value) => {
    const hex = String(value || '').trim().replace('#', '');
    if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return null;
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  };

  // r, g, b in 0-255 -> [hue in degrees, saturation 0-1, lightness 0-1]
  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [h * 60, s, l];
  };

  // -> [r, g, b] in 0-1
  const hslToRgb = (h, s, l) => {
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
    return [f(0), f(8), f(4)];
  };

  // Three shades of one product colour for its background: a light tint, the
  // colour itself and a deeper shade. Grey, black and white products keep their
  // own lightness, so a black hoodie gets a black gradient.
  // - keepLightness: use the colour's lightness as it is
  // - maxLight: never lighter than this (0-1), so white writing on top stays readable
  // - tint: how far the light shade goes towards white (0-1)
  const palette = (rgb, { keepLightness = false, maxLight = 1, tint } = {}) => {
    const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
    const chroma = (Math.max(...rgb) - Math.min(...rgb)) / 255;
    const neutral = chroma < 0.1;
    const sat = neutral ? Math.min(s, 0.12) : s * 0.9;
    let light;
    if (keepLightness) light = clamp(l, 0.06, 0.92);
    // Neutral products sit on a slightly darker (or, if black, slightly lifted) shade so they stand out
    else if (neutral) light = l > 0.3 ? l * 0.8 : Math.max(l, 0.12);
    else light = clamp(l, 0.42, 0.56);
    light = Math.min(light, maxLight);
    const mid = hslToRgb(h, sat, light);
    const lift = tint ?? (neutral ? 0.28 : 0.35);
    return {
      light: mid.map((c) => c + (1 - c) * lift),
      mid,
      deep: hslToRgb(h, Math.min(1, sat * 1.05), light * 0.55),
    };
  };

  // Looks at a small copy of a product photo and returns:
  // - rgb: its main colour. Colourful pixels count most, so a small print doesn't
  //   win over the garment unless the garment itself is neutral.
  // - top / bottom: how much of the photo is see-through empty space above and
  //   below the product (0-1), so "[ Add to cart ]" can sit right under the product.
  const analyses = new Map();
  const analyse = (url) => {
    if (!url) return Promise.resolve(null);
    if (analyses.has(url)) return analyses.get(url);
    const promise = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const w = 48;
          const h = Math.max(1, Math.round((w * img.naturalHeight) / img.naturalWidth));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h).data;
          let r = 0, g = 0, b = 0, total = 0;
          let ar = 0, ag = 0, ab = 0, count = 0;
          let firstRow = -1, lastRow = -1;
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] > 16) {
              const row = Math.floor(i / 4 / w);
              if (firstRow < 0) firstRow = row;
              lastRow = row;
            }
            if (data[i + 3] < 128) continue;
            const R = data[i], G = data[i + 1], B = data[i + 2];
            const max = Math.max(R, G, B), min = Math.min(R, G, B);
            // Skip a plain white photo background
            if (min > 245) continue;
            ar += R; ag += G; ab += B; count++;
            const sat = (max - min) / 255;
            if (sat < 0.2) continue;
            const weight = sat * sat;
            r += R * weight; g += G * weight; b += B * weight; total += weight;
          }
          const rgb = !count ? null : total / count > 0.04 ? [r / total, g / total, b / total] : [ar / count, ag / count, ab / count];
          resolve({
            rgb,
            top: firstRow < 0 ? 0 : firstRow / h,
            bottom: lastRow < 0 ? 0 : (h - 1 - lastRow) / h,
          });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
    analyses.set(url, promise);
    return promise;
  };

  // A product's palette: its hand-picked colour (custom.background_colour) if it has one,
  // otherwise the main colour of its photo. Resolves to null if neither works.
  const productPalette = (hex, img, options) => {
    const manual = parseHex(hex);
    const rgb = manual ? Promise.resolve(manual) : analyse(img).then((result) => result && result.rgb);
    return rgb.then((value) => (value ? palette(value, options) : null));
  };

  /* ---------- Background: slowly flowing gradient in the product's colours, with film grain ---------- */

  const VERTEX = `
    attribute vec2 aPos;
    varying vec2 vUv;
    void main() {
      vUv = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }`;

  const FRAGMENT = `
    #ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    #else
    precision mediump float;
    #endif
    varying vec2 vUv;
    uniform float uTime;
    uniform vec3 uLightL;
    uniform vec3 uMidL;
    uniform vec3 uDeepL;
    uniform vec3 uLightR;
    uniform vec3 uMidR;
    uniform vec3 uDeepR;

    float hash(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
      );
    }

    // Soft round glow centred on c with radii r
    float blob(vec2 p, vec2 c, vec2 r) {
      vec2 d = (p - c) / r;
      return exp(-dot(d, d));
    }

    void main() {
      // y = 0 is the bottom of the screen
      float t = uTime;
      vec2 warp = vec2(noise(vUv * 1.3 + t * 0.04), noise(vUv * 1.3 + 4.7 - t * 0.04)) - 0.5;
      vec2 p = vUv + warp * 0.25;

      // Left and right colours (the same for one product, different for the drop)
      float side = smoothstep(0.38, 0.62, vUv.x + warp.x * 0.12);
      vec3 light = mix(uLightL, uLightR, side);
      vec3 mid = mix(uMidL, uMidR, side);
      vec3 deep = mix(uDeepL, uDeepR, side);

      // Deeper towards the bottom, lighter towards the top
      vec3 color = mix(deep, mid, smoothstep(-0.15, 0.6, p.y));
      color = mix(color, light, smoothstep(0.55, 1.15, p.y) * 0.55);

      // Soft drifting highlights and shade, all in the product's own colours
      vec2 glowA = vec2(0.25 + 0.15 * sin(t * 0.09), 0.72 + 0.14 * cos(t * 0.07));
      vec2 glowB = vec2(0.8 + 0.1 * sin(t * 0.06 + 2.0), 0.35 + 0.2 * cos(t * 0.08 + 1.0));
      vec2 shade = vec2(0.5 + 0.3 * sin(t * 0.05 + 0.5), 0.04 + 0.08 * cos(t * 0.07));
      color = mix(color, light, blob(p, glowA, vec2(0.3, 0.25)) * 0.55);
      color = mix(color, light, blob(p, glowB, vec2(0.28, 0.3)) * 0.45);
      color = mix(color, deep, blob(p, shade, vec2(0.4, 0.22)) * 0.45);

      color += (hash(gl_FragCoord.xy) - 0.5) * 0.085;
      gl_FragColor = vec4(color, 1.0);
    }`;

  const TONES = ['light', 'mid', 'deep'];
  const copyScene = (scene) => ({
    left: Object.fromEntries(TONES.map((k) => [k, [...scene.left[k]]])),
    right: Object.fromEntries(TONES.map((k) => [k, [...scene.right[k]]])),
  });

  class Backdrop {
    constructor(canvas, host) {
      this.canvas = canvas;
      this.host = host;
      this.current = null;
      this.target = null;
      this.time = Math.random() * 100;
      this.frame = this.frame.bind(this);
      this.gl = this.setup();
      if (!this.gl) canvas.style.display = 'none';
      this.onResize = () => this.resize();
      window.addEventListener('resize', this.onResize);
      this.resize();
    }

    setup() {
      const gl = this.canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
      if (!gl) return null;
      const compile = (type, source) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
      };
      const vertex = compile(gl.VERTEX_SHADER, VERTEX);
      const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
      if (!vertex || !fragment) return null;
      const program = gl.createProgram();
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
      gl.useProgram(program);
      // One triangle that covers the whole screen
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'aPos');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      this.uniforms = { time: gl.getUniformLocation(program, 'uTime'), left: {}, right: {} };
      TONES.forEach((tone) => {
        const name = tone[0].toUpperCase() + tone.slice(1);
        this.uniforms.left[tone] = gl.getUniformLocation(program, `u${name}L`);
        this.uniforms.right[tone] = gl.getUniformLocation(program, `u${name}R`);
      });
      return gl;
    }

    resize() {
      if (!this.gl) return;
      const scale = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(1, Math.round(this.canvas.clientWidth * scale));
      const height = Math.max(1, Math.round(this.canvas.clientHeight * scale));
      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
      }
      this.gl.viewport(0, 0, width, height);
      if (this.current) this.draw();
    }

    // scene: { left: palette, right: palette }
    set(scene, instant = false) {
      this.target = scene;
      if (!this.current || instant) this.current = copyScene(scene);
      if (!this.gl) {
        // No WebGL: plain CSS gradient in the same colours
        const css = (c) => `rgb(${c.map((v) => Math.round(v * 255)).join(',')})`;
        this.host.style.background =
          `linear-gradient(to top, ${css(scene.left.deep)}, transparent 60%), ` +
          `linear-gradient(to right, ${css(scene.left.mid)} 40%, ${css(scene.right.mid)} 60%)`;
        return;
      }
      if (!this.running) {
        this.running = true;
        this.last = performance.now();
        requestAnimationFrame(this.frame);
      }
    }

    // Stop animating (e.g. while the basket is closed); set() starts it again
    pause() {
      this.running = false;
    }

    frame(now) {
      if (!this.running) return;
      const dt = Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      if (!reducedMotion()) this.time += dt;
      const k = 1 - Math.exp(-dt * 3.5);
      ['left', 'right'].forEach((side) => {
        TONES.forEach((tone) => {
          const target = this.target[side][tone];
          this.current[side][tone] = this.current[side][tone].map((c, i) => c + (target[i] - c) * k);
        });
      });
      this.draw();
      requestAnimationFrame(this.frame);
    }

    draw() {
      const gl = this.gl;
      gl.uniform1f(this.uniforms.time, this.time);
      ['left', 'right'].forEach((side) => {
        TONES.forEach((tone) => gl.uniform3fv(this.uniforms[side][tone], this.current[side][tone]));
      });
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    destroy() {
      this.running = false;
      window.removeEventListener('resize', this.onResize);
    }
  }

  // Darker than the homepage, so white writing on top (product pages, the basket) stays readable
  const READABLE = { maxLight: 0.4, tint: 0.16 };

  /* ---------- A whole-page background in one product's colours (product pages) ---------- */

  class MeuBackdrop extends HTMLElement {
    connectedCallback() {
      this.backdrop = new Backdrop(this.querySelector('canvas'), this);
      const fallback = palette(parseHex(this.dataset.default) || [36, 40, 51], { ...READABLE, keepLightness: true });
      this.backdrop.set({ left: fallback, right: fallback }, true);
      const started = performance.now();
      productPalette(this.dataset.hex, this.dataset.img, READABLE).then((colours) => {
        // Straight in if it's ready at once; otherwise it eases over from the plain colour
        if (colours) this.backdrop.set({ left: colours, right: colours }, performance.now() - started < 250);
      });
    }

    disconnectedCallback() {
      this.backdrop?.destroy();
    }
  }

  if (!customElements.get('meu-backdrop')) customElements.define('meu-backdrop', MeuBackdrop);

  window.MeuGradient = { clamp, reducedMotion, parseHex, palette, analyse, productPalette, Backdrop, READABLE };
}
